import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { Input } from '../../components/input'
import { Select } from '../../components/select'
import { Textarea } from '../../components/textarea'
import { Checkbox, CheckboxField, CheckboxGroup } from '../../components/checkbox'
import { MultiSelect } from '../../components/multi-select'
import { CREATE_SUBSCRIPTION_PLAN, UPDATE_SUBSCRIPTION_PLAN } from '../../graphql/subscription/mutations'
import {
    GET_SUBSCRIPTION_PLANS,
    GET_SUBSCRIPTION_STATUS,
    GET_SUBSCRIPTION_PERIOD,
} from '../../graphql/subscription/queries'
import { GET_ROUTES_FOR_REPORTS } from '../../graphql/reports/queries'
import { GET_COUNTRY_LIST } from '../../graphql/common/queries'
import { GET_ROUTE_TYPE } from '../../graphql/subscription/queries'
import { ErrorDialog, SuccessDialog } from '../../components/custom/ResponseDialog'
import loadingImage from '../../assets/images/boat-loading.gif'

const SubscriptionForm = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { code } = useParams()
    const isEditMode = !!code

    const [formData, setFormData] = useState({
        subscription_name: { en: '', ar: '' },
        description: { en: '', ar: '' },
        period_code: '',
        amount: '',
        currency: 'BHD',
        category_code: '',
        routes: [],
        trips: [],
        benefits: {
            number_of_trips: '',
            discount_percentage: '',
        },
        valid_from: '',
        valid_to: '',
        status_code: 'DRF',
        metadata: {
            promo_code: '',
            max_subscribers: '',
            featured: false,
            display_order: '',
            terms_conditions: { en: '', ar: '' },
        },
    })

    const [errors, setErrors] = useState({})
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const [routes, setRoutes] = useState([])
    const [countries, setCountries] = useState([])
    const [trips, setTrips] = useState([])
    const [subscriptionStatuses, setSubscriptionStatuses] = useState([])
    const [subscriptionPeriods, setSubscriptionPeriods] = useState([])
    const [routeTypes, setRouteTypes] = useState([])

    const [getRoutes] = useLazyQuery(GET_ROUTES_FOR_REPORTS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getBoatRoute?.success && data?.getBoatRoute?.boat_routes) {
                setRoutes(data.getBoatRoute.boat_routes)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch routes'))
            setShowErrDialog(true)
        },
    })

    const [getCountries] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.country) {
                setCountries(data.country)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch countries'))
            setShowErrDialog(true)
        },
    })

    const [getSubscriptionStatuses] = useLazyQuery(GET_SUBSCRIPTION_STATUS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.subscriptionStatus) {
                setSubscriptionStatuses(data.subscriptionStatus)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch subscription statuses'))
            setShowErrDialog(true)
        },
    })

    const [getSubscriptionPeriods] = useLazyQuery(GET_SUBSCRIPTION_PERIOD, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.subscriptionPeriod) {
                setSubscriptionPeriods(data.subscriptionPeriod)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch subscription periods'))
            setShowErrDialog(true)
        },
    })

    const [getRouteTypes] = useLazyQuery(GET_ROUTE_TYPE, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.routeType) {
                setRouteTypes(data.routeType)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch route types'))
            setShowErrDialog(true)
        },
    })

    const [getSubscriptionDetails, { loading: loadingSubscription }] = useLazyQuery(GET_SUBSCRIPTION_PLANS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getSubscriptionPlans?.subscription_plans?.[0]) {
                const sub = data.getSubscriptionPlans.subscription_plans[0]
                setFormData({
                    subscription_name: sub.subscription_name || { en: '', ar: '' },
                    description: sub.description || { en: '', ar: '' },
                    period_code: sub.period_code || '',
                    amount: sub.amount || '',
                    currency: sub.currency || 'BHD',
                    category_code: sub.category_code || '',
                    routes: sub.routes?.map(r => r.route_id) || [],
                    trips: sub.trips?.map(t => t.trip_id) || [],
                    benefits: {
                        number_of_trips: sub.benefits?.number_of_trips || '',
                        discount_percentage: sub.benefits?.discount_percentage || '',
                    },
                    valid_from: toInputDate(sub.valid_from),
                    valid_to: toInputDate(sub.valid_to),
                    status_code: sub.status_code || 'DRF',
                    metadata: {
                        promo_code: sub.metadata?.promo_code || '',
                        max_subscribers: sub.metadata?.max_subscribers || '',
                        featured: sub.metadata?.featured || false,
                        display_order: sub.metadata?.display_order || '',
                        terms_conditions: sub.metadata?.terms_conditions || { en: '', ar: '' },
                    },
                })
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch subscription details'))
            setShowErrDialog(true)
        },
    })

    const [createSubscription, { loading: createLoading }] = useMutation(CREATE_SUBSCRIPTION_PLAN, {
        onCompleted: data => {
            if (data.createSubscriptionPlan.success) {
                setSuccessMessage(t('Subscription plan created successfully'))
                setShowSucDialog(true)
                setTimeout(() => {
                    navigate('/subscriptions')
                }, 2000)
            } else {
                setErrorMessage(data.createSubscriptionPlan.message || t('Failed to create subscription plan'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('An error occurred while creating subscription plan'))
            setShowErrDialog(true)
        },
    })

    const [updateSubscription, { loading: updateLoading }] = useMutation(UPDATE_SUBSCRIPTION_PLAN, {
        onCompleted: data => {
            if (data.updateSubscriptionPlan.success) {
                setSuccessMessage(t('Subscription plan updated successfully'))
                setShowSucDialog(true)
                setTimeout(() => {
                    navigate('/subscriptions')
                }, 2000)
            } else {
                setErrorMessage(data.updateSubscriptionPlan.message || t('Failed to update subscription plan'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('An error occurred while updating subscription plan'))
            setShowErrDialog(true)
        },
    })

    const ageGroups = [
        { code: 'ADL', label: 'Adult' },
        { code: 'CHD', label: 'Child' },
        { code: 'INF', label: 'Infant' },
        { code: 'SNR', label: 'Senior' },
    ]

    // Load dropdown data and subscription details on mount
    useEffect(() => {
        getCountries({ variables: { code: '' } })
        getRoutes({
            variables: {
                page: { offset: 0, limit: 100 },
                routeId: '',
                routeName: '',
                statusCode: 'ACT',
            },
        })
        getSubscriptionStatuses({ variables: { code: null } })
        getSubscriptionPeriods({ variables: { code: null } })
        getRouteTypes({ variables: { code: '' } })

        // Load subscription details if in edit mode
        if (isEditMode && code) {
            getSubscriptionDetails({
                variables: {
                    filter: { subscription_id: code },
                    page: { offset: 0, limit: 1 },
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Populate trips based on selected routes
    useEffect(() => {
        if (formData.routes?.length > 0) {
            const selectedRoutes = routes.filter(r => formData.routes.includes(r.route_id))
            const allTrips = selectedRoutes.flatMap(route => route.route_trip || [])
            // Remove duplicates based on trip_id
            const uniqueTrips = allTrips.filter(
                (trip, index, self) => index === self.findIndex(t => t.trip_id === trip.trip_id)
            )
            setTrips(uniqueTrips)
        } else {
            setTrips([])
        }
    }, [formData.routes, routes])

    const toInputDate = value => {
        return value ? String(value).split('T')[0] : ''
    }

    // Helper function to remove __typename from objects
    const removeTypename = obj => {
        if (obj === null || obj === undefined) return obj
        if (Array.isArray(obj)) return obj.map(removeTypename)
        if (typeof obj === 'object') {
            const { __typename, ...rest } = obj
            return Object.keys(rest).reduce((acc, key) => {
                acc[key] = removeTypename(rest[key])
                return acc
            }, {})
        }
        return obj
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.subscription_name.en)
            newErrors.subscription_name_en = t('Subscription name (English) is required')
        if (!formData.amount || parseFloat(formData.amount) <= 0) newErrors.amount = t('Valid amount is required')
        if (!formData.valid_from) newErrors.valid_from = t('Valid from date is required')
        if (!formData.valid_to) newErrors.valid_to = t('Valid to date is required')

        if (formData.valid_from && formData.valid_to && formData.valid_from >= formData.valid_to) {
            newErrors.valid_to = t('Valid to date must be after valid from date')
        }

        if (!formData.trips || formData.trips.length === 0) {
            newErrors.trips = t('Please select at least one trip')
        }

        if (!formData.routes || formData.routes.length === 0) {
            newErrors.routes = t('Please select at least one route')
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = e => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        // Clean the data by removing __typename fields
        const cleanedData = removeTypename(formData)

        const input = {
            subscription_name: cleanedData.subscription_name,
            description: cleanedData.description,
            period_code: cleanedData.period_code,
            amount: parseFloat(cleanedData.amount),
            currency: cleanedData.currency,
            category_code: cleanedData.category_code,
            routes: cleanedData.routes,
            trips: cleanedData.trips,
            benefits: {
                number_of_trips: cleanedData.benefits.number_of_trips
                    ? parseInt(cleanedData.benefits.number_of_trips)
                    : null,
                discount_percentage: cleanedData.benefits.discount_percentage
                    ? parseFloat(cleanedData.benefits.discount_percentage)
                    : null,
            },
            valid_from: toInputDate(cleanedData.valid_from),
            valid_to: toInputDate(cleanedData.valid_to),
            status_code: cleanedData.status_code,
            metadata: {
                promo_code: cleanedData.metadata.promo_code || null,
                max_subscribers: cleanedData.metadata.max_subscribers
                    ? parseInt(cleanedData.metadata.max_subscribers)
                    : null,
                featured: cleanedData.metadata.featured,
                display_order: cleanedData.metadata.display_order ? parseInt(cleanedData.metadata.display_order) : null,
                terms_conditions: cleanedData.metadata.terms_conditions,
            },
        }

        if (isEditMode) {
            updateSubscription({ variables: { subscription_id: code, input } })
        } else {
            createSubscription({ variables: { input } })
        }
    }

    if (loadingSubscription) {
        return (
            <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                <img alt="logo" src={loadingImage} className="w-[250px]" />
                <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
            </div>
        )
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/subscriptions')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back to Subscriptions')}
                </button>
            </div>

            <div className="max-w-4xl mx-auto mb-8">
                <form onSubmit={handleSubmit}>
                    <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
                        <div className="px-4 py-6 sm:p-8">
                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                                <div className="sm:col-span-6">
                                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                                        {isEditMode ? t('Edit Subscription Plan') : t('Create Subscription Plan')}
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-gray-600">
                                        {isEditMode
                                            ? t('Update the subscription plan details and benefits')
                                            : t('Add a new subscription plan with benefits')}
                                    </p>
                                </div>

                                {/* Basic Information */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900">{t('Basic Information')}</h3>
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Status')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.status_code}
                                        onChange={e => setFormData({ ...formData, status_code: e.target.value })}
                                        required
                                    >
                                        <option value="">{t('Select Status')}</option>
                                        {subscriptionStatuses.map(status => (
                                            <option key={status.code} value={status.code}>
                                                {status.description?.en || status.code}
                                            </option>
                                        ))}
                                    </Select>
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Subscription Name (English)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="text"
                                        value={formData.subscription_name.en}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                subscription_name: {
                                                    ...formData.subscription_name,
                                                    en: e.target.value,
                                                },
                                            })
                                        }
                                        required
                                    />
                                    {errors.subscription_name_en && (
                                        <p className="text-red text-xs mt-1">{errors.subscription_name_en}</p>
                                    )}
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Subscription Name (Arabic)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="text"
                                        value={formData.subscription_name.ar}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                subscription_name: {
                                                    ...formData.subscription_name,
                                                    ar: e.target.value,
                                                },
                                            })
                                        }
                                        dir="rtl"
                                        required
                                    />
                                </div>

                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Description (English)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Textarea
                                        value={formData.description.en}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                description: { ...formData.description, en: e.target.value },
                                            })
                                        }
                                        rows={3}
                                        required
                                    />
                                </div>

                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Description (Arabic)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Textarea
                                        value={formData.description.ar}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                description: { ...formData.description, ar: e.target.value },
                                            })
                                        }
                                        rows={3}
                                        dir="rtl"
                                        required
                                    />
                                </div>

                                {/* Pricing & Category */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900 mt-4">
                                        {t('Pricing & Category')}
                                    </h3>
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Amount')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={formData.amount}
                                        onChange={e => setFormData({ ...formData, amount: e.target.value })}
                                        required
                                    />
                                    {errors.amount && <p className="text-red text-xs mt-1">{errors.amount}</p>}
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Currency')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.currency}
                                        onChange={e => setFormData({ ...formData, currency: e.target.value })}
                                        required
                                    >
                                        <option value="BHD">BHD</option>
                                        <option value="USD">USD</option>
                                        <option value="EUR">EUR</option>
                                    </Select>
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Period')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.period_code}
                                        onChange={e => setFormData({ ...formData, period_code: e.target.value })}
                                        required
                                    >
                                        <option value="">{t('Select Period')}</option>
                                        {subscriptionPeriods.map(period => (
                                            <option key={period.code} value={period.code}>
                                                {period.description?.en || period.code}
                                            </option>
                                        ))}
                                    </Select>
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Route Category')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.category_code}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                category_code: e.target.value,
                                                routes: [],
                                                trips: [],
                                            })
                                        }
                                        required
                                    >
                                        <option value="">{t('Select Route Category')}</option>
                                        {routeTypes.map(type => (
                                            <option key={type.code} value={type.code}>
                                                {type.description?.en || type.code}
                                            </option>
                                        ))}
                                    </Select>
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Valid From')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        min={new Date().toISOString().split('T')[0]}
                                        type="date"
                                        value={toInputDate(formData.valid_from)}
                                        onChange={e => setFormData({ ...formData, valid_from: e.target.value })}
                                        required
                                    />
                                    {errors.valid_from && <p className="text-red text-xs mt-1">{errors.valid_from}</p>}
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Valid To')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="date"
                                        min={new Date().toISOString().split('T')[0]}
                                        value={toInputDate(formData.valid_to)}
                                        onChange={e => setFormData({ ...formData, valid_to: e.target.value })}
                                        required
                                    />
                                    {errors.valid_to && <p className="text-red text-xs mt-1">{errors.valid_to}</p>}
                                </div>

                                {/* Eligibility */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900 mt-4">{t('Eligibility')}</h3>
                                </div>

                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                        {t('Routes')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <MultiSelect
                                        placeholder={t('Select routes')}
                                        value={formData.routes || []}
                                        onChange={values =>
                                            setFormData({
                                                ...formData,
                                                routes: values,
                                            })
                                        }
                                        required
                                        options={routes
                                            .filter(
                                                route =>
                                                    route.master_route?.route_type?.code ===
                                                    (formData.category_code === 'DMC' ? 'DMC' : 'INL')
                                            )
                                            .map(route => ({
                                                value: route.route_id,
                                                label: route.route_name?.en || route.route_name,
                                            }))}
                                        disabled={formData.category_code?.length === 0}
                                    />
                                    {errors.routes && <p className="text-xs text-red-500 mt-1">{errors.routes}</p>}
                                </div>

                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                        {t('Trips')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <MultiSelect
                                        placeholder={t('Select trips')}
                                        value={formData.trips || []}
                                        onChange={values =>
                                            setFormData({
                                                ...formData,
                                                trips: values,
                                            })
                                        }
                                        options={trips.map(trip => ({
                                            value: trip.trip_id,
                                            label: trip.trip_name?.en || trip.trip_name,
                                        }))}
                                        disabled={formData.routes?.length === 0}
                                        required
                                    />
                                    {formData.routes?.length === 0 && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {t('Please select routes first to see available trips')}
                                        </p>
                                    )}
                                    {errors.trips && <p className="text-xs text-red-500 mt-1">{errors.trips}</p>}
                                </div>

                                {/* Benefits */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900 mt-4">{t('Benefits')}</h3>
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Number of Trips')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="number"
                                        value={formData.benefits.number_of_trips}
                                        min={0}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                benefits: { ...formData.benefits, number_of_trips: e.target.value },
                                            })
                                        }
                                        required
                                    />
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Discount Percentage')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="number"
                                        min={0}
                                        step="0.01"
                                        value={formData.benefits.discount_percentage}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                benefits: { ...formData.benefits, discount_percentage: e.target.value },
                                            })
                                        }
                                        required
                                    />
                                </div>

                                {/* Metadata */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900 mt-4">
                                        {t('Application Settings')}
                                    </h3>
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Subscription Reference Code')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="text"
                                        value={formData.metadata.promo_code}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                metadata: { ...formData.metadata, promo_code: e.target.value },
                                            })
                                        }
                                        required
                                    />
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Max Subscribers')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="number"
                                        min={0}
                                        value={formData.metadata.max_subscribers}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                metadata: { ...formData.metadata, max_subscribers: e.target.value },
                                            })
                                        }
                                        required
                                    />
                                </div>

                                {/* <div className="sm:col-span-6">
                                    <CheckboxField>
                                        <Checkbox
                                            checked={formData.metadata.featured}
                                            onChange={checked =>
                                                setFormData({
                                                    ...formData,
                                                    metadata: { ...formData.metadata, featured: checked },
                                                })
                                            }
                                        />
                                        <label className="text-sm font-medium">{t('Featured Plan')}</label>
                                    </CheckboxField>
                                </div> */}

                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Terms & Conditions (English)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Textarea
                                        value={formData.metadata.terms_conditions.en}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                metadata: {
                                                    ...formData.metadata,
                                                    terms_conditions: {
                                                        ...formData.metadata.terms_conditions,
                                                        en: e.target.value,
                                                    },
                                                },
                                            })
                                        }
                                        rows={4}
                                        required
                                    />
                                </div>

                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Terms & Conditions (Arabic)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Textarea
                                        value={formData.metadata.terms_conditions.ar}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                metadata: {
                                                    ...formData.metadata,
                                                    terms_conditions: {
                                                        ...formData.metadata.terms_conditions,
                                                        ar: e.target.value,
                                                    },
                                                },
                                            })
                                        }
                                        rows={4}
                                        dir="rtl"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
                            <button
                                type="button"
                                onClick={() => navigate('/subscriptions')}
                                className="text-sm font-semibold leading-6 text-gray-900"
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={createLoading || updateLoading}
                                className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
                            >
                                {createLoading || updateLoading
                                    ? t('Saving...')
                                    : isEditMode
                                      ? t('Update Subscription')
                                      : t('Create Subscription')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {showSucDialog && (
                <SuccessDialog
                    status={showSucDialog}
                    title={t('Success')}
                    message={successMessage}
                    onClose={() => {
                        setShowSucDialog(false)
                        setSuccessMessage('')
                    }}
                />
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={showErrDialog}
                    title={t('Error')}
                    message={errorMessage}
                    onClose={() => {
                        setShowErrDialog(false)
                        setErrorMessage('')
                    }}
                />
            )}
        </>
    )
}

export default SubscriptionForm
